import React from 'react';
import { Mail, MapPin, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useLanguage } from '../../context/LanguageContext';

const ContactSection = () => {
  const { t } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(t('contact.success'));
  };

  return (
    <section id="contact" className="relative py-24 bg-[#060b1a]">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#4DFF88]/5 rounded-full blur-[200px]" />
      </div>

      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#4DFF88]/50 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full border border-[#4DD6FF]/30 bg-[#4DD6FF]/5 text-[#4DD6FF] text-sm font-medium mb-4">
            {t('contact.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-white mb-6">
                {t('contact.infoTitle')}
              </h3>
              <p className="text-gray-400 mb-8 leading-relaxed">
                {t('contact.infoText')}
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/5 hover:border-[#4DFF88]/30 transition-colors duration-300">
                <div className="w-12 h-12 rounded-lg bg-[#4DFF88]/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#4DFF88]" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">{t('contact.email')}</p>
                  <a
                    href="mailto:csresearcheryouth@gmail.com"
                    className="text-white hover:text-[#4DFF88] transition-colors"
                  >
                    csresearcheryouth@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/5 hover:border-[#4DD6FF]/30 transition-colors duration-300">
                <div className="w-12 h-12 rounded-lg bg-[#4DD6FF]/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#4DD6FF]" />
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">{t('contact.location')}</p>
                  <p className="text-white">Zonguldak / Türkiye</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="relative">
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#4DFF88]/30 via-[#4DD6FF]/20 to-[#FF4DA6]/30 blur-sm" />
            <form
              onSubmit={handleSubmit}
              className="relative p-8 rounded-2xl bg-[#0a1628] border border-white/10"
            >
              <h3 className="text-xl font-semibold text-white mb-6">
                {t('contact.formTitle')}
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">{t('contact.name')}</label>
                    <Input
                      type="text"
                      placeholder={t('contact.namePlaceholder')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#4DFF88] rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">{t('contact.emailLabel')}</label>
                    <Input
                      type="email"
                      placeholder={t('contact.emailPlaceholder')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#4DFF88] rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">{t('contact.subject')}</label>
                  <Input
                    type="text"
                    placeholder={t('contact.subjectPlaceholder')}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#4DFF88] rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">{t('contact.message')}</label>
                  <Textarea
                    placeholder={t('contact.messagePlaceholder')}
                    rows={4}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#4DFF88] rounded-lg resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#4DFF88] hover:bg-[#4DFF88]/10 hover:text-[#4DFF88] text-[#060b1a] font-medium py-3 rounded-none transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  {t('contact.send')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
